<template>
    <div v-if="crumbs.length > 0" class="nc-breadcrumbs">
        <button class="nc-crumb nc-crumb--home" @click="goHome">
            <mdicon name="home-outline" :size="14" />
        </button>
        <template v-for="(crumb, i) in crumbs" :key="i">
            <mdicon name="chevron-right" :size="12" class="nc-crumb-sep" />
            <button
                class="nc-crumb"
                :class="{ 'nc-crumb--current': i === crumbs.length - 1 }"
                @click="crumb.action && crumb.action()"
                :disabled="!crumb.action"
            >
                {{ crumb.label }}
            </button>
        </template>
    </div>
</template>

<script>
import { db } from '@/services/db';

// Route names grouped by navigation depth
const INSPECTION_ROUTES = new Set([
    'inspection-show', 'inspection-information', 'close-inspection',
    'location-item-tree', 'location-systems', 'documents',
    'forms-inspection', 'inspection-checklists', 'regulations',
    'inspection-interval', 'measurement-instruments', 'findings-list',
    'addresses',
]);
const COMPONENT_ROUTES = new Set([
    'location-item-component-edit', 'sub-location-item-component-edit',
    'component-details', 'forms-location-item-component',
    'location-item-component-checklists', 'samples', 'scios-codes',
    'location-item-edit', 'location-item-create',
]);
const DETAIL_ROUTES = new Set([
    'observations', 'observation-create', 'observation-show', 'observation-edit',
    'measurements', 'measurement-create', 'measurement-show', 'measurement-edit',
    'measurement-bulk', 'image-gallery',
]);

// All deep routes combined
const ALL_DEEP = new Set([...INSPECTION_ROUTES, ...COMPONENT_ROUTES, ...DETAIL_ROUTES]);
const COMPONENT_OR_DEEPER = new Set([...COMPONENT_ROUTES, ...DETAIL_ROUTES]);

export default {
    name: 'NavBreadcrumbs',
    data() {
        return {
            inspectionName: null,
        };
    },
    computed: {
        inspectionId() {
            return this.$store.getters.getInspectionId;
        },
        routeName() {
            return this.$route.name;
        },
        isDeepRoute() {
            return ALL_DEEP.has(this.routeName);
        },
        crumbs() {
            const name = this.routeName;
            if (!name || !this.isDeepRoute) return [];

            const items = [];
            const isSmall = window.innerWidth < 400;
            const nameMax = isSmall ? 14 : 22;

            // Level 1: Inspection name
            items.push({
                label: this.truncate(this.inspectionName || 'Inspectie', nameMax),
                action: this.inspectionId ? () => this.$router.push({ name: 'inspection-show', params: { id: this.inspectionId } }) : null,
            });

            // Level 2: Bouwdelen
            if (COMPONENT_OR_DEEPER.has(name)) {
                items.push({
                    label: 'Bouwdelen',
                    action: this.inspectionId ? () => this.$router.push({ name: 'location-item-tree', params: { id: this.inspectionId } }) : null,
                });
            }

            // Level 3: Component name (from query param)
            if (DETAIL_ROUTES.has(name)) {
                const componentName = this.$route.query.location_item_component_name;
                if (componentName) {
                    items.push({
                        label: this.truncate(componentName, isSmall ? 12 : 18),
                        action: null,
                    });
                }
            }

            return items;
        },
    },
    watch: {
        inspectionId: {
            async handler(id) {
                if (!id) { this.inspectionName = null; return; }
                try {
                    const inspection = await db.inspections.get(parseInt(id));
                    this.inspectionName = inspection?.name || 'Inspectie';
                } catch {
                    this.inspectionName = 'Inspectie';
                }
            },
            immediate: true,
        },
    },
    methods: {
        goHome() {
            this.$router.push('/root/inspections');
        },
        truncate(str, max) {
            if (!str) return '';
            return str.length > max ? str.substring(0, max) + '...' : str;
        },
    },
};
</script>

<style scoped>
.nc-breadcrumbs {
    display: flex;
    align-items: center;
    gap: 2px;
    padding: 4px 16px;
    background: var(--nc-bg-page);
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    flex-shrink: 0;
}

.nc-breadcrumbs::-webkit-scrollbar {
    display: none;
}

.nc-crumb {
    display: flex;
    align-items: center;
    gap: 4px;
    border: none;
    background: none;
    color: var(--nc-color-primary);
    font-family: var(--nc-font-family);
    font-size: 13px;
    font-weight: 500;
    padding: 2px 4px;
    border-radius: var(--nc-radius-sm, 4px);
    cursor: pointer;
    white-space: nowrap;
    -webkit-tap-highlight-color: transparent;
}

.nc-crumb:active {
    background: var(--nc-color-primary-light, rgba(0, 128, 128, 0.08));
}

.nc-crumb:disabled {
    color: var(--nc-text-muted);
    cursor: default;
}

.nc-crumb:disabled:active {
    background: none;
}

.nc-crumb--home {
    color: var(--nc-text-muted);
    padding: 4px;
    border-radius: var(--nc-radius-full, 50%);
}

.nc-crumb--home:active {
    background: var(--nc-color-light);
}

.nc-crumb--current {
    color: var(--nc-text-muted);
    cursor: default;
}

.nc-crumb-sep {
    color: var(--nc-text-muted);
    opacity: 0.5;
    flex-shrink: 0;
}
</style>
